// src/app/components/admin/wind-tunnel/wind-tunnel-run-detail.tsx
//
// One wind-tunnel run: live progress while it runs (polled), the controls
// (cancel toggle, resume, add another draft, post-publish retrieval
// re-check), and the report. The report LEADS with one scores table — beliefs
// and named / top five / primary down the side, control and each draft across
// — and puts each draft's verdict under it, worded as a read of the table with
// the rule beside it; then retrieval arms, an optional comparison with a prior
// result, counts/cost/keys, and each draft's copy.
// The report is rebuilt from stored rows, so a stopped run reads the same way
// a finished one does, marked provisional.

import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Activity, BarChart3, Download, FileText, FlaskConical, GitCompare, Globe, Loader2, Pause, Play, Plus, Receipt,
  RotateCcw, Search, ShieldCheck,
} from "lucide-react";

import {
  windTunnelApi,
  type WindTunnelEstimate,
  type WindTunnelReport,
  type WindTunnelRun,
  type WindTunnelTableRow,
} from "@/api/wind-tunnel";
import {
  Button, ErrorNote, Input, PageHeading, SHADE_BG, Section, StatusText, VerdictChip, helpStyle, labelStyle, num, pct,
  shortDate, usd,
} from "./wind-tunnel-shared";
import { DraftEditor } from "./wind-tunnel-new-test";

// The backend refuses a fifth draft; this only hides the form at the limit.
const MAX_DRAFTS = 4;

const icon = (Icon: typeof Activity, color = "var(--alpha-teal)") => (
  <Icon style={{ width: 16, height: 16, color }} />
);

const POLL_MS = 4000;

const th = "px-2 py-2 text-left";
const td = "px-2 py-2 align-top";
const tableText = { fontSize: "var(--text-sm)", color: "var(--foreground)" } as const;

function Progress({ run }: { run: WindTunnelRun }) {
  const total = Math.max(1, run.calls_total);
  const spent = (run.probe_cost_usd || 0) + (run.judge_cost_usd || 0);
  const cap = run.config_json?.spend_cap_usd;
  const bar = (value: number, color: string) => (
    <div className="h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: "var(--muted, #f1f5f9)" }}>
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, (value / total) * 100)}%`, backgroundColor: color }} />
    </div>
  );
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div>
        <div className="flex justify-between" style={helpStyle}>
          <span>Collected</span><span>{run.calls_completed}/{run.calls_total}</span>
        </div>
        {bar(run.calls_completed, "var(--signal-blue)")}
      </div>
      <div>
        <div className="flex justify-between" style={helpStyle}>
          <span>Judged</span><span>{run.calls_scored}/{run.calls_total}</span>
        </div>
        {bar(run.calls_scored, "var(--alpha-teal)")}
      </div>
      <div>
        <div className="flex justify-between" style={helpStyle}>
          <span>Spent</span><span>{usd(spent)}{cap ? ` of ${usd(cap)}` : ""}</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full" style={{ backgroundColor: "var(--muted, #f1f5f9)" }}>
          <div className="h-full rounded-full" style={{
            width: `${cap ? Math.min(100, (spent / cap) * 100) : 0}%`,
            backgroundColor: cap && spent >= cap ? "var(--infrared-coral)" : "var(--spectral-violet)",
          }} />
        </div>
      </div>
    </div>
  );
}

function cellValue(value: number | null | undefined, scale: "mean" | "rate"): string {
  return scale === "mean" ? num(value) : pct(value);
}

function cellDelta(delta: number | null | undefined, scale: "mean" | "rate"): string {
  if (delta === null || delta === undefined) return "—";
  return scale === "mean" ? num(delta, 3, true) : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}pp`;
}

/** The lead of the report: beliefs, then named / top five / primary, down the
 * side; control and each draft across. Each draft cell shows its score and
 * its difference from control, tinted by size at the gate's own lines. */
function ScoresTable({ report }: { report: WindTunnelReport }) {
  const table = report.table!;
  const [open, setOpen] = useState<string | null>(null);
  const laneLabel = new Map(report.config.lanes.map((lane) => [lane.lane, lane.label]));
  const drafts = table.columns.slice(1);
  const beliefs = table.rows.filter((row) => row.kind === "belief");
  const visibility = table.rows.filter((row) => row.kind === "visibility");
  const [meanWarn, meanFail] = table.shade_lines.mean;
  const [rateWarn, rateFail] = table.shade_lines.rate_pp;

  const renderRow = (row: WindTunnelTableRow) => {
    const gains = drafts
      .map((column) => row.drafts[column.id])
      .filter((cell) => cell && cell.delta !== null && cell.shade?.startsWith("gain"))
      .map((cell) => cell.delta as number);
    const best = gains.length && drafts.length > 1 ? Math.max(...gains) : null;
    const expanded = open === row.id;
    return (
      <Fragment key={row.id}>
        <tr
          style={{ borderBottom: "1px solid var(--border)", cursor: "pointer" }}
          onClick={() => setOpen(expanded ? null : row.id)}
          title="Click for the per-model split"
        >
          <td className={td} style={{ minWidth: 160 }}>
            <div style={{ fontWeight: "var(--font-weight-medium)" }}>{row.label}</div>
            <div style={helpStyle}>{expanded ? "by model ▴" : "by model ▾"}</div>
          </td>
          <td className={`${td} text-right tabular-nums`} title={`n = ${row.control.n}`}>
            {cellValue(row.control.value, row.scale)}
          </td>
          {drafts.map((column) => {
            const cell = row.drafts[column.id];
            if (!cell) return <td key={column.id} className={`${td} text-right`}>—</td>;
            const isBest = best !== null && cell.delta === best;
            const tip = [
              `n = ${cell.n}`,
              cell.ci95 ? `95% interval ${num(cell.ci95[0], 2, true)} … ${num(cell.ci95[1], 2, true)} (shown, not gated on)` : null,
              cell.status ? `gate: ${cell.status}` : null,
              isBest ? "largest move in this row" : null,
            ].filter(Boolean).join(" · ");
            return (
              <td
                key={column.id}
                className={`${td} text-right tabular-nums`}
                title={tip}
                style={{ backgroundColor: cell.shade ? SHADE_BG[cell.shade] : undefined }}
              >
                <div style={{ fontWeight: isBest ? 700 : "var(--font-weight-medium)" }}>
                  {cellValue(cell.value, row.scale)}
                </div>
                <div style={{
                  fontSize: 12,
                  color: cell.delta === null ? "var(--muted-foreground)"
                    : cell.delta < 0 ? "var(--error-text)" : cell.delta > 0 ? "var(--success-text)" : "var(--muted-foreground)",
                  fontWeight: cell.shade ? 600 : 400,
                }}>
                  {cellDelta(cell.delta, row.scale)}
                </div>
              </td>
            );
          })}
        </tr>
        {expanded && (
          <tr style={{ borderBottom: "1px solid var(--border)", backgroundColor: "var(--muted, #f8fafc)" }}>
            <td className={td} colSpan={2 + drafts.length}>
              {row.claim && <p style={{ ...helpStyle, marginBottom: 6 }}>{row.claim}</p>}
              <table style={{ ...helpStyle, width: "100%" }}>
                <tbody>
                  {Object.entries(row.by_lane).map(([lane, stats]) => (
                    <tr key={lane}>
                      <td className="py-0.5 pr-3">{laneLabel.get(lane) || lane}</td>
                      <td className="py-0.5 pr-3 text-right tabular-nums">{cellValue(stats.control as number | null, row.scale)}</td>
                      {drafts.map((column) => {
                        const entry = stats[column.id] as { value: number | null; delta: number | null } | undefined;
                        return (
                          <td key={column.id} className="py-0.5 pr-3 text-right tabular-nums">
                            {entry ? `${cellValue(entry.value, row.scale)} (${cellDelta(entry.delta, row.scale)})` : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const swatch = (band: keyof typeof SHADE_BG, text: string) => (
    <span className="inline-flex items-center gap-1.5">
      <span style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: SHADE_BG[band], border: "1px solid var(--border)" }} />
      {text}
    </span>
  );

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full" style={tableText}>
          <thead style={{ ...helpStyle, borderBottom: "1px solid var(--border)" }}>
            <tr>
              <th className={th}>Belief</th>
              {table.columns.map((column) => (
                <th key={column.id} className={`${th} text-right`} style={{ whiteSpace: "nowrap" }}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {beliefs.map(renderRow)}
            <tr>
              <td colSpan={2 + drafts.length} className="px-2 pt-4 pb-1" style={{ ...helpStyle, fontWeight: 600 }}>
                Visibility — share of answers
              </td>
            </tr>
            {visibility.map(renderRow)}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5" style={helpStyle}>
        {swatch("gain", `gain past ${meanWarn.toFixed(2)} (rates ${rateWarn}pp)`)}
        {swatch("gain_strong", `gain past ${meanFail.toFixed(2)} (${rateFail}pp)`)}
        {swatch("loss", `loss past ${meanWarn.toFixed(2)} (${rateWarn}pp)`)}
        {swatch("loss_strong", `loss past ${meanFail.toFixed(2)} (${rateFail}pp)`)}
        <span>Unshaded = within ±{meanWarn.toFixed(2)} of control.{drafts.length > 1 ? " Bold = largest move in its row." : ""}</span>
      </div>
    </>
  );
}

/** Each draft's verdict, worded as our read of the table, with the rule
 * it applied next to it. The gate logic is the backend's, unchanged. */
function VerdictSection({ report }: { report: WindTunnelReport }) {
  return (
    <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,320px)]">
      <ul className="flex flex-col gap-3">
        {report.drafts.map((draft) => (
          <li key={draft.id} className="flex flex-wrap items-start gap-3">
            <VerdictChip verdict={draft.verdict} provisional={!report.complete} />
            <div className="min-w-0 flex-1">
              <div style={labelStyle}>{draft.label}</div>
              <p style={{ ...helpStyle, color: "var(--foreground)", fontSize: 13 }}>{draft.read}</p>
            </div>
          </li>
        ))}
      </ul>
      <aside className="rounded-lg p-3" style={{ backgroundColor: "var(--muted, #f8fafc)", border: "1px solid var(--border)" }}>
        <p style={{ ...labelStyle, marginBottom: 4 }}>Rule applied</p>
        <p style={helpStyle}>{report.verdict_rule}</p>
      </aside>
    </div>
  );
}

function Report({ report }: { report: WindTunnelReport }) {
  const laneLabel = new Map(report.config.lanes.map((lane) => [lane.lane, lane.label]));
  return (
    <div className="flex flex-col gap-4">
      {report.table && (
        <Section
          icon={icon(BarChart3, "var(--signal-blue)")}
          title="Scores — control and each draft"
          hint="Beliefs: mean judge score per answer, 0 = claim not made, 1 = implied, 2 = explicit reason to choose. Visibility: share of answers that name the brand, place it in the top five, or make it the primary pick. Every draft cell shows its difference from control. Chat models pooled; click a row for the per-model split."
        >
          <ScoresTable report={report} />
        </Section>
      )}

      {report.gated && report.drafts.length > 0 && (
        <Section icon={icon(ShieldCheck, "var(--signal-green)")} title="Verdict — our read of the table">
          <VerdictSection report={report} />
        </Section>
      )}

      {report.retrieval.length > 0 && (
        <Section icon={icon(Globe, "var(--spectral-violet)")} title="Retrieval arms" hint="Question only, live retrieval. Reported against the brand's last read — not gated.">
          <div className="overflow-x-auto">
            <table className="w-full" style={tableText}>
              <thead style={{ ...helpStyle, borderBottom: "1px solid var(--border)" }}>
                <tr>
                  <th className={th}>Model</th>
                  <th className={`${th} text-right`}>Mention rate</th>
                  <th className={`${th} text-right`}>Last read</th>
                  <th className={`${th} text-right`}>Pre-publish run</th>
                </tr>
              </thead>
              <tbody>
                {report.retrieval.map((row) => (
                  <tr key={row.lane} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td className={td}>{laneLabel.get(row.lane) || row.lane}</td>
                    <td className={`${td} text-right tabular-nums`}>{pct(row.rate)} <span style={helpStyle}>({row.n})</span></td>
                    <td className={`${td} text-right tabular-nums`}>
                      {row.prior_read ? (
                        <>
                          {pct(row.prior_read.rate)}{" "}
                          <span style={helpStyle}>
                            read #{row.prior_read.run_sequence_number}
                            {row.prior_read.same_questions ? "" : ", all questions"}
                            {row.delta_pp_vs_read !== null ? ` · ${row.delta_pp_vs_read > 0 ? "+" : ""}${row.delta_pp_vs_read.toFixed(1)}pp` : ""}
                          </span>
                        </>
                      ) : <span style={helpStyle}>no prior read</span>}
                    </td>
                    <td className={`${td} text-right tabular-nums`}>
                      {row.parent ? (
                        <>
                          {pct(row.parent.rate)}{" "}
                          <span style={helpStyle}>
                            #{row.parent.run_id}
                            {row.delta_pp_vs_parent !== null ? ` · ${row.delta_pp_vs_parent > 0 ? "+" : ""}${row.delta_pp_vs_parent.toFixed(1)}pp` : ""}
                          </span>
                        </>
                      ) : <span style={helpStyle}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {report.reference && (
        <Section
          icon={icon(GitCompare, "var(--signal-blue)")}
          title={`Compared with: ${report.reference.label}`}
          hint={`Checked against ${report.reference.draft_label || "the first draft"}. Within noise = the difference is inside this run's per-question interval widened by √2 for the prior run's own sampling error.`}
          aside={
            <div className="flex flex-wrap items-center gap-2" style={helpStyle}>
              <span>Prior verdict</span><VerdictChip verdict={(report.reference.reference_verdict as any) || null} />
              <span>{report.reference.verdict_match === null ? "" : report.reference.verdict_match ? "· same verdict" : "· DIFFERENT verdict"}</span>
            </div>
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full" style={tableText}>
              <thead style={{ ...helpStyle, borderBottom: "1px solid var(--border)" }}>
                <tr>
                  <th className={th}>Belief</th>
                  <th className={`${th} text-right`}>Prior delta</th>
                  <th className={`${th} text-right`}>This run</th>
                  <th className={`${th} text-right`}>Difference</th>
                  <th className={th}>Within noise</th>
                </tr>
              </thead>
              <tbody>
                {report.reference.variables.map((row) => (
                  <tr key={row.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td className={td}>{row.label}</td>
                    <td className={`${td} text-right tabular-nums`}>{num(row.reference_delta, 3, true)}</td>
                    <td className={`${td} text-right tabular-nums`}>{num(row.delta, 3, true)}</td>
                    <td className={`${td} text-right tabular-nums`}>{num(row.difference, 3, true)} <span style={helpStyle}>±{num(row.tolerance)}</span></td>
                    <td className={td}>{row.within_noise === null ? "—" : row.within_noise ? "yes" : "NO"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <Section icon={icon(Receipt)} title="Run record">
        <div className="grid gap-4 sm:grid-cols-3" style={tableText}>
          <div>
            <p style={helpStyle}>Calls</p>
            <p>{report.counts.calls_collected}/{report.counts.calls_total} collected</p>
            <p style={helpStyle}>
              {report.counts.calls_errored} errored · {report.counts.calls_not_attempted} not attempted
            </p>
            <p style={helpStyle}>
              {report.counts.scored} judged · {report.counts.failed_zeroed} judge-failed (zero) · {report.counts.excluded_no_answer} excluded (no answer)
            </p>
          </div>
          <div>
            <p style={helpStyle}>Cost</p>
            <p>{usd(report.cost.total_usd)}{report.cost.spend_cap_usd ? <span style={helpStyle}> of {usd(report.cost.spend_cap_usd)} cap</span> : null}</p>
            <p style={helpStyle}>probe {usd(report.cost.probe_usd)} · judge {usd(report.cost.judge_usd)}</p>
            {report.drafts.length > 0 && (
              <>
                <p style={helpStyle}>Control {usd(report.control_cost?.total_usd)}</p>
                {report.drafts.map((draft) => (
                  <p key={draft.id} style={helpStyle}>{draft.label} {usd(draft.cost.total_usd)}</p>
                ))}
              </>
            )}
          </div>
          <div>
            <p style={helpStyle}>Models · judge · keys</p>
            {report.config.lanes.map((lane) => (
              <p key={lane.lane} style={helpStyle}>{lane.label}: {lane.model_version || "unversioned"}{lane.web_search ? " (web)" : ""}</p>
            ))}
            <p style={helpStyle}>Judge: {report.config.judge.model_string}</p>
            <p style={helpStyle}>Keys: {Object.entries(report.config.keys).map(([provider, source]) => `${provider} ${source}`).join(", ") || "—"}</p>
          </div>
        </div>
        {report.config.injection_mode && <p style={{ ...helpStyle, marginTop: 10 }}>Injection: {report.config.injection_mode}.</p>}
      </Section>

      {report.drafts.map((draft) => (
        <Section
          key={draft.id}
          icon={icon(FileText, "var(--spectral-violet)")}
          title={`Appendix — ${draft.label} (verbatim)`}
          hint={`${draft.chars.toLocaleString()} characters · sha256 ${draft.sha256?.slice(0, 16)}…${draft.added_at ? ` · added ${shortDate(draft.added_at)}` : ""}${draft.added_by ? ` by ${draft.added_by}` : ""}`}
        >
          <pre className="whitespace-pre-wrap rounded-lg p-3" style={{ ...helpStyle, color: "var(--foreground)", backgroundColor: "var(--muted, #f8fafc)", fontFamily: "inherit" }}>
            {draft.text}
          </pre>
        </Section>
      ))}
    </div>
  );
}

/** Append another draft arm to a finished run: same questions, models,
 * passes and judge; the stored control arm is reused, not re-run. */
function AddDraftSection({
  run, onAdd, busy,
}: {
  run: WindTunnelRun;
  onAdd: (body: { label?: string; text: string; spend_cap_usd: number }) => void;
  busy: boolean;
}) {
  const report = run.report_json!;
  const index = report.drafts.length;
  const [draft, setDraft] = useState({ label: "", text: "" });
  const [cap, setCap] = useState("");
  const [capTouched, setCapTouched] = useState(false);
  const [estimate, setEstimate] = useState<WindTunnelEstimate | null>(null);

  const chatLanes = report.config.lanes.filter((lane) => lane.role === "chat");
  const estimateKey = JSON.stringify([run.id, chatLanes.map((lane) => [lane.lane, lane.model_version]), report.config.questions, report.config.passes]);
  useEffect(() => {
    let alive = true;
    windTunnelApi
      .estimate({
        lanes: chatLanes.map((lane) => ({ lane: lane.lane, model_version: lane.model_version })),
        arms: ["injection"],
        questions: report.config.questions,
        passes: report.config.passes,
        scorer_model_id: report.config.judge.scorer_model_id,
        drafts: 1,
      })
      .then((next) => {
        if (!alive) return;
        setEstimate(next);
        if (!capTouched) setCap(String(next.suggested_cap_usd));
      })
      .catch(() => alive && setEstimate(null));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateKey]);

  return (
    <Section
      icon={icon(Plus, "var(--spectral-violet)")}
      title="Add another draft"
      hint={`Runs one more arm on this run's ${report.config.questions} questions, ${chatLanes.length} chat model${chatLanes.length === 1 ? "" : "s"} and ${report.config.passes} passes, and adds a column to the table. Control and the existing drafts are not re-run.`}
      aside={<span style={helpStyle}>{index} of {MAX_DRAFTS}</span>}
    >
      <DraftEditor index={index} draft={draft} onChange={setDraft} />
      <div className="mt-3 flex flex-wrap items-end gap-6">
        <div style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>
          {estimate ? (
            <>
              <div><strong>{estimate.calls.toLocaleString()}</strong> new calls · est. <strong>{usd(estimate.total_usd)}</strong></div>
              <div style={helpStyle}>catalog estimate; the run records measured spend</div>
            </>
          ) : <span style={helpStyle}>Estimating…</span>}
        </div>
        <div>
          <label style={labelStyle} htmlFor="wt-add-cap">Spend cap for this draft (USD)</label>
          <Input id="wt-add-cap" type="number" min={1} className="mt-1 w-28"
            value={cap} onChange={(e) => { setCap(e.target.value); setCapTouched(true); }} />
        </div>
        <Button
          variant="primary"
          disabled={busy || !draft.text.trim() || !(Number(cap) > 0)}
          onClick={() => onAdd({ label: draft.label.trim() || undefined, text: draft.text, spend_cap_usd: Number(cap) })}
        >
          <Plus className="h-4 w-4" /> Run this draft
        </Button>
      </div>
    </Section>
  );
}

export function WindTunnelRunDetail({
  runId,
  onOpenRun,
  onChanged,
}: {
  runId: number;
  onOpenRun: (runId: number) => void;
  onChanged: () => void;
}) {
  const [run, setRun] = useState<WindTunnelRun | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [newCap, setNewCap] = useState("");
  const [recheckCap, setRecheckCap] = useState("");

  const load = useCallback(() => {
    windTunnelApi.get(runId).then(setRun).catch((e) => setErr(String(e?.message || e)));
  }, [runId]);

  useEffect(() => {
    setRun(null);
    setErr("");
    load();
  }, [load]);

  const live = run && (run.status === "pending" || run.status === "running");
  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [live, load]);

  async function act(action: () => Promise<WindTunnelRun>, openResult = false) {
    setBusy(true);
    setErr("");
    try {
      const next = await action();
      onChanged();
      if (openResult && next.id !== runId) onOpenRun(next.id);
      else setRun(next);
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  if (!run) {
    return err ? <ErrorNote>{err}</ErrorNote> : (
      <div className="flex items-center gap-2" style={helpStyle}><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
    );
  }

  const report = run.report_json || null;
  const spent = (run.probe_cost_usd || 0) + (run.judge_cost_usd || 0);
  const canResume = run.status === "cancelled" || run.status === "failed" || run.stale;
  const cappedOut = run.stop_reason === "spend_cap";
  const isRecheck = run.parent_run_id !== null;

  return (
    <div className="flex flex-col gap-4">
      <PageHeading
        icon={<FlaskConical style={{ width: 22, height: 22, color: "var(--alpha-teal)" }} />}
        title={<>{run.brand_name} <span style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--muted-foreground)" }}>#{run.id}</span></>}
        subtitle={
          <>
            {run.name || (isRecheck ? "Retrieval re-check" : "Content test")}
            {" · "}{run.source === "brand" ? "onboarded brand" : "pasted inputs"}
            {" · "}{shortDate(run.created_at)}{run.created_by ? ` by ${run.created_by}` : ""}
            {" · "}<StatusText status={run.status} stopReason={run.stop_reason} stale={run.stale} />
            {isRecheck && (
              <>
                {" · "}
                <button type="button" onClick={() => onOpenRun(run.parent_run_id!)}
                  style={{ fontSize: 12, color: "var(--muted-foreground)", background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>
                  re-check of test #{run.parent_run_id}
                </button>
              </>
            )}
          </>
        }
      />

      <Section icon={icon(Activity, "var(--signal-blue)")} title="Progress">
        <Progress run={run} />
        {run.error_message && <div className="mt-3"><ErrorNote>{run.error_message}</ErrorNote></div>}
        <div className="mt-4 flex flex-wrap items-end gap-2">
          {live && !run.stale && (
            run.cancel_requested ? (
              <Button onClick={() => act(() => windTunnelApi.cancel(run.id, false))} disabled={busy}>
                <Play className="h-4 w-4" /> Undo cancel
              </Button>
            ) : (
              <Button variant="danger" onClick={() => act(() => windTunnelApi.cancel(run.id, true))} disabled={busy}
                title="Stops dispatching new calls. What was collected stays readable.">
                <Pause className="h-4 w-4" /> Cancel
              </Button>
            )
          )}
          {live && run.cancel_requested && <span style={helpStyle}>Stopping after the calls in flight…</span>}
          {canResume && (
            <>
              {cappedOut && (
                <div>
                  <label style={labelStyle} htmlFor="wt-newcap">New cap (USD)</label>
                  <Input id="wt-newcap" type="number" min={Math.ceil(spent) + 1}
                    className="mt-1 w-28"
                    value={newCap} onChange={(e) => setNewCap(e.target.value)} placeholder={String(Math.ceil(spent * 2) || 10)} />
                </div>
              )}
              <Button onClick={() => act(() => windTunnelApi.resume(run.id, newCap ? Number(newCap) : undefined))}
                disabled={busy || (cappedOut && !(Number(newCap) > spent))}
                title="Continue where it stopped — completed calls are not repeated.">
                <RotateCcw className="h-4 w-4" /> Resume
              </Button>
            </>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button onClick={() => windTunnelApi.download(run.id, "txt").catch((e) => setErr(String(e?.message || e)))}>
              <Download className="h-4 w-4" /> Report
            </Button>
            <Button onClick={() => windTunnelApi.download(run.id, "json").catch((e) => setErr(String(e?.message || e)))}>
              <Download className="h-4 w-4" /> JSON
            </Button>
            <Button onClick={() => windTunnelApi.download(run.id, "csv").catch((e) => setErr(String(e?.message || e)))}>
              <Download className="h-4 w-4" /> Calls CSV
            </Button>
          </div>
        </div>
        {err && <div className="mt-3"><ErrorNote>{err}</ErrorNote></div>}
      </Section>

      {run.status === "completed" && !isRecheck && report?.gated && report.drafts.length < MAX_DRAFTS && (
        <AddDraftSection
          key={`${run.id}-${report.drafts.length}`}
          run={run}
          busy={busy}
          onAdd={(body) => act(() => windTunnelApi.addDraft(run.id, body))}
        />
      )}

      {run.status === "completed" && !isRecheck && (
        <Section
          icon={icon(Search, "var(--spectral-violet)")}
          title="After publish: retrieval re-check"
          hint="Once the content is live, re-run the retrieval arms (ChatGPT Web, AI Mode — question only) as a new run that compares back to this one. This run is not changed."
        >
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label style={labelStyle} htmlFor="wt-recheck-cap">Spend cap (USD)</label>
              <Input id="wt-recheck-cap" type="number" min={1} className="mt-1 w-28"
                value={recheckCap} onChange={(e) => setRecheckCap(e.target.value)} placeholder="10" />
            </div>
            <Button onClick={() => act(() => windTunnelApi.recheck(run.id, Number(recheckCap)), true)} disabled={busy || !(Number(recheckCap) > 0)}>
              <Search className="h-4 w-4" /> Start re-check
            </Button>
          </div>
        </Section>
      )}

      {report ? <Report report={report} /> : <p style={helpStyle}>The report appears once the first answers are judged.</p>}
    </div>
  );
}
